import { useTranslation } from 'react-i18next';

import {
  Box,
  DialogContainer,
  Divider,
  List,
  ListItem,
  Typography,
} from '@frontierag/frontier-ui-library';
import { Warning as WarningIcon } from '@frontierag/frontier-ui-library/icons';
import { useAppSettings } from 'contexts/App/useAppSettings';
import { v4 as uuidv4 } from 'uuid';

import { styles } from './styles';
import { type IActionRequiredModalProps, ProductContext } from './types';

const ActionRequiredModal: React.FC<IActionRequiredModalProps> = ({
  open,
  onClose,
  products = [],
  limusPerformProducts = [],
}) => {
  const { t } = useTranslation('ActionRequired', { keyPrefix: 'dialog' });
  const { FERTILISER: { liquidNotificationDateRange } = {} } = useAppSettings();

  const allProducts = [
    { context: ProductContext.photoID, products },
    { context: ProductContext.limusPerform, products: limusPerformProducts },
  ].filter((p) => !!p.products.length);

  return (
    <DialogContainer
      open={open}
      onClose={onClose}
      customHeader={<Header allProducts={allProducts} />}
      closeLabel={t('confirmBtn')}
      closeButtonProps={{
        variant: 'contained',
      }}
    >
      {allProducts.map(({ context, products }, index) => {
        const isLast = index === allProducts.length - 1;

        return (
          <Box key={context}>
            {allProducts.length > 1 && (
              <Typography variant="subtitle3" data-testid={`${context}-subtitle`}>
                {t('subtitle', { context, index: index + 1 })}
              </Typography>
            )}
            <Typography variant="body2" color="grey.L10" data-testid={`${context}-description`}>
              {t('description', {
                context,
                startDate: liquidNotificationDateRange?.startDayMonth,
                endDate: liquidNotificationDateRange?.endDayMonth,
              })}
            </Typography>
            <Box sx={styles.box}>
              <List sx={styles.ul}>
                {products.map((product) => (
                  <ListItem key={uuidv4()} sx={styles.li}>
                    <Typography variant="body2" fontWeight="700">
                      {product}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
            {!isLast && <Divider sx={{ my: 1.5 }} />}
          </Box>
        );
      })}
    </DialogContainer>
  );
};

const Header = ({ allProducts }) => {
  const { t } = useTranslation('ActionRequired', { keyPrefix: 'dialog' });

  let context = ProductContext.multi;
  if (allProducts.length === 1) context = allProducts[0].context;

  return (
    <Box sx={styles.header}>
      <WarningIcon width={24} sx={styles.warningIcon} />
      <Typography variant="h3">{t('title', { context })}</Typography>
    </Box>
  );
};

export default ActionRequiredModal;
